import React, { useCallback, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import FolderCommentsComponent from './FolderCommentsComponent';
import { useMutation } from '@apollo/client';
import { DELETE_RISK_COMMENT_MUTATION, ADD_OR_UPDATE_RISK_COMMENT_MUTATION } from '../mutations';
import { GET_RISK_COMMENTS_QUERY } from '../queries';
import { showError } from 'actions/actionCreators/snackbarActions';
import { useServerErrorHandling } from 'utils/hooks/useServerErrorHandling';
import { useQueryWithErrorHandling } from 'customHooks/errorHandling';
import { useGlobalSubscription } from 'providers/GlobalCacheManager';
import { RISK_COMMENT_UPDATED } from '../subscriptions';
import { Grid } from '@mui/material';
import { RightsContext } from 'providers/RightsContext';
import { useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { get } from '@totalsoft/react-state-lens';
import { Button, FakeText } from '@totalsoft/rocket-ui';
import { useToast } from 'utils/hooks';
const defaultConfig = { page: 1, pageSize: 5, totalCount: 0 };

const FolderCommentsContainer = ({ documentId, siteId }) => {
  const { t } = useTranslation();
  const [currentComment, setCurrentComment] = useState(null);
  const [localRiskComment, setLocalRiskComment] = useState(null);
  const { riskAssesmentNotesWrite, readOnlyFull } = useContext(RightsContext);
  const [pagination, setPagination] = useState(defaultConfig);
  const rowsPerPageOptions = [5, 10, 20];
  const [commentsLens, , resetCommentLens] = useChangeTrackingLens([]);
  const commentsList = commentsLens |> get;
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const addToast = useToast();

  const { loading, data, refetch } = useQueryWithErrorHandling(GET_RISK_COMMENTS_QUERY, {
    variables: { documentId, siteId },
    fetchPolicy: 'network-only',
    onCompleted: data => {
      resetCommentLens(getItemsPerPage(data.getRiskComments, pagination.pageSize, pagination.page));
      if (data.getRiskComments != null)
        setLocalRiskComment(data.getRiskComments.slice().sort((a, b) => b.riskCommentId - a.riskCommentId)[0]?.freeTxt);
    }
  });

  const getItemsPerPage = useCallback(
    (comments, pageSize, page) => {
      const current = (page - 1) * pageSize;

      setPagination({
        ...pagination,
        totalCount: comments.length,
        page: defaultConfig.page
      });
      return comments.slice(current, current + pageSize);
    },
    [pagination]
  );

  const { storeKey } = useServerErrorHandling({
    effect: _ => {
      setSaving(false);
    }
  });

  const [addOrUpdateRiskComment] = useMutation(ADD_OR_UPDATE_RISK_COMMENT_MUTATION, {
    onError: error => {
      addToast(error.message, 'error');
    },
    onCompleted: data => {
      const { correlationId } = data;
      storeKey({ correlationId });
    }
  });

  const saveComment = useCallback(
    (comment, text) => {
      setSaving(true);
      addOrUpdateRiskComment({
        variables: {
          input: {
            riskCommentId: comment?.riskCommentId,
            freeTxt: text,
            documentId,
            siteId
          }
        }
      });
    },
    [addOrUpdateRiskComment, documentId, siteId]
  );

  const [deleteRiskComment] = useMutation(DELETE_RISK_COMMENT_MUTATION, {
    onError: error => {
      showError(error.message);
    },
    onCompleted: data => {
      const { correlationId } = data;
      storeKey({ correlationId });
    }
  });

  const deleteComment = useCallback(
    riskCommentId => {
      deleteRiskComment({
        variables: {
          input: { documentId, siteId, riskCommentId }
        }
      });
    },
    [deleteRiskComment, documentId, siteId]
  );

  useGlobalSubscription(RISK_COMMENT_UPDATED, {
    variables: {
      documentId,
      siteId
    },
    onSubscriptionData: () => {
      deleteCurrentComment();
      setSaving(false);
      refetch();
    }
  });

  const deleteCurrentComment = useCallback(() => {
    setCurrentComment(null);
  }, []);

  const onRiskCommentChanged = e => {
    setLocalRiskComment(e);
    setIsDirty(true);
  };

  const addComment = useCallback(() => {
    setSaving(true);
    addOrUpdateRiskComment({
      variables: {
        input: {
          riskCommentId: null,
          freeTxt: localRiskComment,
          documentId,
          siteId
        }
      }
    });
    setIsDirty(false);
  }, [addOrUpdateRiskComment, documentId, localRiskComment, siteId]);

  const changeCurrentComment = useCallback(
    prop => value => {
      setCurrentComment({ ...currentComment, [prop]: value });
    },
    [currentComment]
  );

  const updatePageSize = useCallback(
    (items, pagination) => {
      const page = pagination.page;
      const pageSize = pagination.pageSize;
      resetCommentLens(getItemsPerPage(items, pageSize, page));
    },
    [getItemsPerPage, resetCommentLens]
  );

  const onChangePage = useCallback(
    page => {
      const newPage = ++page;
      updatePageSize(data.getRiskComments, { ...pagination, page: newPage });
      setPagination({ ...pagination, page: newPage });
    },
    [data?.getRiskComments, pagination, updatePageSize]
  );

  const onChangeRowsPerPage = useCallback(
    size => {
      if (commentsList.length < size) {
        updatePageSize(data.getRiskComments, { ...pagination, pageSize: size, page: defaultConfig.page });
        setPagination({ ...pagination, pageSize: size, page: defaultConfig.page });
      } else {
        updatePageSize(data.getRiskComments, { ...pagination, pageSize: size });
        setPagination({ ...pagination, pageSize: size });
      }
    },
    [commentsList?.length, data?.getRiskComments, pagination, updatePageSize]
  );

  const onPaginationRefresh = useCallback(() => {
    updatePageSize(data.getRiskComments, {
      ...pagination,
      pageSize: defaultConfig.pageSize
    });
    setPagination({ ...defaultConfig, totalCount: data.getRiskComments.length });
  }, [data, pagination, updatePageSize]);

  const editingItem = Boolean(currentComment);

  return (
    <Grid container>
      <Grid item xs={12} mb={2}>
        <Button
          id={'offerNotes_addBtn'}
          onClick={addComment}
          color='primary'
          variant='contained'
          disabled={readOnlyFull || !riskAssesmentNotesWrite || editingItem || saving || !isDirty}
        >
          {t('Save')}
        </Button>
      </Grid>
      {loading ? (
        <FakeText lines={6} />
      ) : (
        <FolderCommentsComponent
          commentsLens={commentsLens}
          localRiskComment={localRiskComment}
          onRiskCommentChanged={onRiskCommentChanged}
          onRemoveItem={deleteComment}
          onSaveItem={saveComment}
          currentComment={currentComment}
          changeCurrentComment={changeCurrentComment}
          removeCurrentItem={deleteCurrentComment}
          rowsPerPageOptions={rowsPerPageOptions}
          onChangeRowsPerPage={onChangeRowsPerPage}
          onChangePage={onChangePage}
          onRefresh={onPaginationRefresh}
          pagination={pagination}
          setCurrentComment={setCurrentComment}
          saving={saving}
        />
      )}
    </Grid>
  );
};

FolderCommentsContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired
};

export default FolderCommentsContainer;
